<template>
  <section class="overflow-hidden">
    <div class="relative px-4 mx-auto max-w-5xl xl:px-0 md:px-6">
      <div>
        <h2 class="mx-auto max-w-3xl text-2xl text-center md:leading-10 xl:text-42 md:text-4xl font-ww-bold" v-html="mainTitle"></h2>

        <div
          class="mx-auto mt-5 max-w-3xl text-base lg:pr-60 2xl:max-w-3xl md:mt-12 xl:text-xl md:text-lg font-ww-regular lg:text-left 2xl:pr-0 xl:pr-12">
          {{ introText }}
          <br><br>

          {{ listHeading }}

          <ul class="pl-6 mt-3 space-y-6 list-disc text-left md:mt-6 custom-marker-small">
            <li v-for="(item, index) in normalizedListItems" :key="index">
              <strong>{{ item.label }}</strong>
              
              → {{ item.description }}
            </li>
          </ul>

          <div class="mt-8 lg:pr-32" v-html="closingText"></div>
        </div>

        <div class="mt-10 lg:mt-0">
          <nuxt-img :src="imageSrc" :alt="imageAlt"
            class="top-32 mx-auto 2xl:top-40 lg:absolute lg:-right-16 xl:-right-52 2xl:-right-64" format="webp"
            loading="lazy" width="490" height="616" sizes="xs:293px sm:393px md:490px lg:490px xl:490px 2xl:490px" />
        </div>

        <div class="flex justify-center pb-10 md:pb-20">
          <button @click="scrollTo(scrollTarget, scrollPosition(mobilePosition))" type="button"
            :style="{ background: primaryColor }"
            class="text-white bnt-basic items-center mt-12 flex xl:!py-5 font-bold !text-lg md:!py-4 !py-3 gap-x-2 !capitalize md-max:px-8 md:!text-xl lg:!text-3xl md:!px-16"
            :aria-label="ctaText">
            <span>{{ ctaText }}</span>
            <svg width="28" height="23" viewBox="0 0 28 23" fill="none" xmlns="http://www.w3.org/2000/svg"
              class="relative top-0.5 w-5 md:w-6 lg:top-1 lg:w-auto" aria-hidden="true">
              <path fill-rule="evenodd" clip-rule="evenodd"
                d="M27.3403 12.5758L17.9164 22.0853C17.787 22.2166 17.6327 22.321 17.4625 22.3922C17.2923 22.4634 17.1096 22.5 16.925 22.5C16.7405 22.5 16.5578 22.4634 16.3876 22.3922C16.2174 22.321 16.0631 22.2166 15.9337 22.0853C15.6706 21.8187 15.5231 21.4594 15.5231 21.0852C15.5231 20.7109 15.6706 20.3517 15.9337 20.0851L22.905 13.0496H1.90953C1.60041 13.0478 1.30464 12.9237 1.08722 12.7043C0.869793 12.4849 0.748502 12.1883 0.750014 11.8797V11.3841C0.748502 11.0755 0.869793 10.7789 1.08722 10.5596C1.30464 10.3402 1.60041 10.216 1.90953 10.2143H23.1608L15.9289 2.91494C15.6659 2.6483 15.5184 2.28907 15.5184 1.91482C15.5184 1.54056 15.6659 1.18133 15.9289 0.914689C16.0583 0.783352 16.2126 0.679029 16.3828 0.607828C16.553 0.536628 16.7358 0.5 16.9203 0.5C17.1048 0.5 17.2875 0.536628 17.4577 0.607828C17.6279 0.679029 17.7822 0.783352 17.9116 0.914689L27.3233 10.4119C27.5559 10.6493 27.6968 10.9612 27.7211 11.2924C27.7679 11.5211 27.7577 11.7578 27.6913 11.9816C27.6249 12.2054 27.5043 12.4095 27.3403 12.5758Z"
                fill="white" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, toRefs } from 'vue'
import { scrollTo } from '@/composables/scrollTo'
import { scrollPosition } from '@/composables/utils'
const props = defineProps({
  mainTitle: {
    type: String,
    default: '"Meet the Coffee That Does More <br>Than Wake You Up."'
  },
  introText: {
    type: String,
    default: "We didn't set out to make just another coffee. We set out to make a smarter one — coffee that helps you feel amazing now and supports your brain health for years to come."
  },
  listHeading: {
    type: String,
    default: "That's why Lifeboost Mushroom Coffee combines:"
  },
  listItems: {
    type: Array,
    default: () => [
      "Organic Low Acid Specialty Coffee → smooth, low-acid, gentle on your stomach",
      "Lion's Mane Mushroom → studied for its ability to support memory, focus, and long-term brain health",
      "Chaga Mushroom → packed with antioxidants to strengthen your immune system",
      "3rd Party Tested for 450+ Toxins → every bag is third-party tested for mycotoxins, molds, heavy metals, pesticides, glyphosate, acrylamide, etc"
    ]
  },
  closingText: {
    type: String,
    default: "The result?<br><br>The rich, bold taste of real coffee — with the added power of mushrooms your brain and body will thank you for."
  },
  imageSrc: {
    type: String,
    default: "/assets/images/gognition-right.png"
  },
  imageAlt: {
    type: String,
    default: "Lifeboost Mushroom Coffee product"
  },
  ctaText: {
    type: String,
    default: "Try It Risk-Free Today"
  },
  primaryColor: {
    type: String,
    default: "#ff9315"
  },
  scrollTarget: {
    type: String,
    default: "the-products"
  },
  mobilePosition: {
    type: String,
    default: "bottom"
  }
})

// expose props to template as refs (improves IDE type inference)
const {
  mainTitle,
  introText,
  listHeading,
  closingText,
  imageSrc,
  imageAlt,
  ctaText,
  primaryColor,
  scrollTarget,
  mobilePosition
} = toRefs(props)

const normalizedListItems = computed(() => {
  return (props.listItems || []).map((raw) => {
    if (typeof raw === 'string') {
      const parts = raw.split('→')
      return {
        label: (parts[0] || '').trim(),
        description: (parts[1] || '').trim()
      }
    }
    return {
      label: raw?.label ?? '',
      description: raw?.description ?? ''
    }
  })
})

defineBuilderComponent({
  friendlyName: "Content - Description CTA Image",
  inputs: [
    {
      name: "mainTitle",
      type: "longText",
      defaultValue: '"Meet the Coffee That Does More <br>Than Wake You Up."'
    },
    {
      name: "introText",
      type: "longText",
      defaultValue: "We didn't set out to make just another coffee. We set out to make a smarter one — coffee that helps you feel amazing now and supports your brain health for years to come."
    },
    {
      name: "listHeading",
      type: "text",
      defaultValue: "That's why Lifeboost Mushroom Coffee combines:"
    },
    {
      name: "listItems",
      type: "list",
      subFields: [
        {
          name: "label",
          type: "text",
          required: true
        },
        {
          name: "description",
          type: "text",
          required: true
        }
      ],
      defaultValue: [
        { label: "Organic Low Acid Specialty Coffee", description: "smooth, low-acid, gentle on your stomach" },
        { label: "Lion's Mane Mushroom", description: "studied for its ability to support memory, focus, and long-term brain health" },
        { label: "Chaga Mushroom", description: "packed with antioxidants to strengthen your immune system" },
        { label: "3rd Party Tested for 450+ Toxins", description: "every bag is third-party tested for mycotoxins, molds, heavy metals, pesticides, glyphosate, acrylamide, etc" }
      ]
    },
    {
      name: "closingText",
      type: "longText",
      defaultValue: "The result?<br><br>The rich, bold taste of real coffee — with the added power of mushrooms your brain and body will thank you for."
    },
    {
      name: "imageSrc",
      type: "file",
      allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
      defaultValue: "/assets/images/gognition-right.png"
    },
    {
      name: "imageAlt",
      type: "text",
      defaultValue: "Lifeboost Mushroom Coffee product"
    },
    {
      name: "ctaText",
      type: "text",
      defaultValue: "Try It Risk-Free Today"
    },
    {
      name: "primaryColor",
      type: "color",
      defaultValue: "#ff9315"
    },
    {
      name: "scrollTarget",
      type: "text",
      defaultValue: "the-products"
    },
    {
      name: "mobilePosition",
      type: "enum",
      friendlyName: "Scroll Position Mobile",
      enum: [
        { label: "Bottom", value: "bottom" },
        { label: "Top", value: "top" }
      ],
      defaultValue: "bottom"
    }
  ]
})
</script>
